"use strict"

//import {keysExist, objectMap} from '../util.js';
import {Synchronizer} from './synchronizer.js';
import { ModalConfirm } from "./modals/modalConfirm.js";
import { SettingsManager } from './settingsManager.js';

class SinResponseError extends Error {
  constructor(message) {
    super(message); // (1)
    this.name = "SinResponseError";
  }
}

class AppDataError extends Error {
  constructor(message) {
    super(message); // (1)
    this.name = "AppDataError";
  }
}

export class DataManager {

   constructor(sinServer, app) {

      this.app = app;
      this.sinServer = sinServer;
      this.users = null;
      this.classes = null;
      this.online = false;
      this.userGroups = null;
      this.appTypes = null;
      this.syncing = false;
      this.dayCacheMaxSize = 100;
      this.clientSettings = new SettingsManager('clientSettings', () => {
         if (this.online) this.sinRequest({ action: 'client-settings-save', clientSettings: this.clientSettings.exportData() });
      });

      this.debug = {
         logRequests: true
      }

   }

   async init() {

      this.ready = false;
      window.db = new Dexie('sinapp');

      await db.version(2).stores({
         years:                 '&id, name, dateFrom, dateTo, isCurrent',
         appointmentTypes:      '&id, type',
         appointments:          '++localId, &id, yearId, typeId, dateFrom, dateTo, creator, lastModTime, syncStatus',
         classRegister:         '++localId, &id, yearId, creatorId, syncStatus, date, *classes',
         classRegisterDayCache: 'dateClassKey, date, classId, time',
         classNotes:            '++localId, &id, yearId, creatorId, syncStatus, dateFrom, dateTo, *classes',
         //absences:              '++localId, &id, yearId, dateFrom, dateTo, classId',
         pupilNotes:            '++localId, &id, yearId, dateFrom, dateTo, classId',
         users:                 '&id, type, short, lastName',
         userGroups:            '&id, type, name',
         courses:               '&id, short, subjectId',
         classes:               '&id, year, ext, name',
         subjects:              '&id, short, name',
         timetable:             '&id, weekday, hour, courseId',
         resources:             '&id, status',
         resourceReservations:  '&id, year, day, hour, userShort, creatorId, userId',
         rooms:                 '&id, short',
         substitutions:         '++id',
         tagesplaene:           '++id, date, page',
         watches:               '&id, teacherId',
         themes:                '&id, name',
         keyValueData:          '&key'
      });

      //if (!localStorage.getItem('clientId')) localStorage.setItem('clientId', this.createRandomId(40));
      this.clientSettings.loadFromLocalStorage();

      this.timeFrames = {
         regular: {
            am: [
               { start: '8:05', end: '8:50' },
               { start: '8:50', end: '9:35' },
               { start: '9:50', end: '10:35' },
               { start: '10:40', end: '11:25' },
               { start: '11:35', end: '12:20' },
               { start: '12:25', end: '13:10' }
            ],
            sek1_7: { start: '13:15', end: '14:00' },
            pm: [
               { start: '13:35', end: '14:20' },
               { start: '14:20', end: '15:05' },
               { start: '15:05', end: '15:50' },
               { start: '15:50', end: '16:35' }
            ],
            break: [
               { start: '08:00', end: '08:05' },
               { start: '09:35', end: '09:50' },
               { start: '11:25', end: '11:35' },
               { start: '13:10', end: '13:25' }
            ]
         },
         short: {
            am: [
               { start: '8:05', end: '8:35' },
               { start: '8:40', end: '9:10' },
               { start: '9:25', end: '9:55' },
               { start: '10:00', end: '10:30' },
               { start: '10:40', end: '11:10' },
               { start: '11:15', end: '11:45' }
            ],
            sek1_7: { start: '12:00', end: '12:30' },
            pm: [
               { start: '12:00', end: '12:30' },
               { start: '12:30', end: '13:00' },
               { start: '13:00', end: '13:30' },
               { start: '13:30', end: '14:00' }
            ],
            break: [
               { start: '08:00', end: '08:05' },
               { start: '09:10', end: '09:25' },
               { start: '10:30', end: '10:40' },
               { start: '11:45', end: '12:00' }
            ]
         }         
      };


   }

   async dropDatabase() {
      if (!window.db) window.db = new Dexie('sinapp');
      await db.delete();
   }

   async recreate() {
      try {
         const data = await this.getSyncData(true, false);
         
         if (data) {
            await this.dropDatabase();
            localStorage.setItem('lastSync', 0);
            await this.init();
            await this.synchronize(data);
         }
         
      }
      catch(e) {
         logger.error(e);
         toast.alert(e, { duration: 8000 });
      }
      finally {
         progress.show(false);
      }

   }

   isReady() {
      return this.ready;
   }

   async createCache(forceUpdate) {

      await this.cacheRooms(forceUpdate);
      await this.cacheClasses(forceUpdate);
      await this.cacheCourses(forceUpdate);
      await this.cacheUsers(forceUpdate);
      await this.cachePupils(forceUpdate);
      await this.cacheTimetable(forceUpdate);
      await this.cacheUserGroups(forceUpdate);
      await this.cacheAppointmentTypes(forceUpdate);
      await this.cacheCurrentYear(forceUpdate);
      this.ready = true;
      this.user = this.getUserSelf();

   }

   async requestUserData(short, password) {

      localStorage.setItem('authToken', '');

      progress.setValue(0, 0, "Lade Benutzerdaten");
      let sinResponse = await this.sinRequest({ action: 'usr-get-data', 'user-short': short, 'pwd': password }, progress);

      this.setupUser(sinResponse.data);
      if (sinResponse.authToken) localStorage.setItem('authToken', sinResponse.authToken);
      return sinResponse.data;

   }

   setupUser(user) {
      if (user.type == 'pupil') {
         this.saveMyClasses([user.class.id]);
      }
      user.settings = (user.customData && user.customData != '') ? JSON.parse(user.customData) : {};
      delete user.customData;
      localStorage.setItem('user', JSON.stringify(user));
   }

   async getSyncData(full, quiet) {

      let lastSync = full ? 0 : localStorage.getItem('lastSync');
      if (lastSync === "undefined") lastSync = 0;

      progress.show(!quiet);

      try {

         if (this.syncing) throw new Error("Es wird bereits synchronisiert.");
         this.syncing = true;

         let syn = new Synchronizer(this);
         const uploadData = await syn.getData();

         let sinResponse = await this.sinRequest({ action: 'synchronize', lastSync: lastSync, updates: uploadData }, progress);

         if (!sinResponse || !sinResponse.data) {
            return false;
         }
         else {
            if (!quiet) {
               for (const message of sinResponse.notes) {
                  logger.message(message);
                  toast.message(message);
               }
            }
            return sinResponse.data;
         }
      }
      catch(e) {
         logger.error(e);
         toast.alert(e, { duration: 8000 });
      }
      finally {
         progress.show(false);
         this.syncing = false;
      }

   }

   async synchronize(data, quiet) {

      if (!data) return false;

      let newDataFound = false;
      progress.show(!quiet);

      try {
         if (data.userData) {
            this.setupUser(data.userData);
         }

         newDataFound = Object.keys(data.get).length > 0 || Object.keys(data.set).length > 0;

         if (newDataFound) {
            let syn = new Synchronizer(this);
            await syn.saveData(data, progress);
            await this.createCache(true);
            localStorage.setItem('lastSync', data.lastSync);            
         }

      }
      catch(e) {
         logger.error(e);
         toast.alert(e, { duration: 8000 });
      }
      finally {
         if (Object.keys(data.get).filter(key => key !== 'userClientSettings').length === 0) {
            toast.message("Es wurden keine neuen Daten gefunden.", { duration: 1000 });
         }
         else {
            toast.message('Synchronisierung angeschlossen.', { duration: 1000 });
         }
         progress.show(false);
         this.syncing = false;
      }

      return newDataFound;

   }

   async setUserEmail(email) {

      const result = await this.sinRequest({ action: 'usr-save-data', 'user-data': { email: email }});

   }

   async requestNewPassword(userShort) {

      let sinResponse = await this.sinRequest({ action: 'usr-request-new-pwd', 'user-short': userShort });
      return sinResponse.data;

   }

   async getAppointmentsInRange(yearId, range) {

      let from = range.from.isoDate(), to = range.to.isoDate();

      return await db.appointments
         .where("yearId").equals(yearId)
         .filter(item => { return (item.dateFrom >= from && item.dateFrom <= to) || (item.dateTo >= from && item.dateTo <= to) })
         .toArray();

   }



   /*
   async getAppointmentTypes() {

      let appTypes = {};
      for (let type of await db.appointmentTypes.toArray()) {
         appTypes[type.id] = type;
      }
      return appTypes;

   }
   */

   async getYearList() {
      return await db.years.toArray();
   }

   async getYear(id) {
      return await db.years.get({id: id});
   }

   getCurrentYear() {
      return this.currentYear;
   }

   setTimeFrameType(type) {
      localStorage.setItem('timeFrameType', type);
   }

   getTimeFrames() {
      const timeFrameType = localStorage.getItem('timeFrameType') || 'regular';
      return this.timeFrames[timeFrameType];
   }


   getMyClasses() {
      return localStorage.getItem('myClasses') || [];
   }

   getCalFilters() {
      let db = JSON.parse(localStorage.getItem('calFilters')) || { };
      let def = {
         classes: 'all',
         appTypes: 'all',
         onlyMy: false,
         getClassFilters: function(yearId) {
            if (this.classes === 'all') return 'all';
            else return this.classes[yearId] || 'all';
         },
         setClassFilters: function(yearId, classes) {
            if (typeof this.classes !== 'object') this.classes = {};
            this.classes[yearId] = classes;
         }
      };
      let filters = Object.extend(true, def, db);
      return filters;

   }

   getUserSelf() {
      let user = JSON.parse(localStorage.getItem('user')) || null;
      if (user) {
         user.hasPermission = function(type, action) {
            return user.permissions.hasOwnProperty(type) && user.permissions[type].hasOwnProperty(action) && user.permissions[type][action];
         }
      }

      return user;
   }

   setCurrentThemeName(theme) {
      localStorage.setItem('app-theme', theme);
   }

   getCurrentThemeName() {
      return localStorage.getItem('app-theme') || 'Standard';
   }

   async getCurrentTheme() {
      return await (db.themes.where({name: this.getCurrentThemeName()}).first());
   }

   async getThemes() {
      const themes = await (db.themes.toArray());
      const themeChoices = await this.getKeyValueData('themeChoices');
      themes.map(theme => {
         theme.cntUsers = themeChoices.hasOwnProperty(theme.id) ? themeChoices[theme.id] : 0;
      });
      themes.sort((a, b) => {
         if (a.cntUsers > b.cntUsers) return -1;
         if (a.cntUsers < b.cntUsers) return  1;
         return 0;
      });
      return themes;
   }

   setFontSize(fontSize) {
      localStorage.setItem('app-font-size', fontSize);
   }

   getFontSize() {
      return localStorage.getItem('app-font-size') || '15px';
   }

   isLoginVerified() {
      const token = localStorage.getItem('authToken');
      return token && token.length !== 0;
   }

   /*
   setSyncOnInit(sync) {
      localStorage.setItem('syncOnInit', sync ? 1 : 0);
   }

   getSyncOnInit() {
      const sync = localStorage.getItem('syncOnInit');
      return sync && parseInt(sync) !== 0 ? true : false;
   }
   */

   getDebugMode() {
      const mode = localStorage.getItem('debugMode');
      return mode && parseInt(mode) !== 0 ? true : false;
   }

   setDebugMode(debugMode) {
      localStorage.setItem('debugMode', debugMode ? 1 : 0);
   }

   getLastTab() {
      return localStorage.getItem('sinappCurrentTab') || 'subst-my';
   }

   async getKeyValueData(key) {
      const data = await db.keyValueData.get(key);
      return data ? data.value : null;
   }

   async setKeyValueData(key, value) {
      await db.keyValueData.put({ key: key, value: value});
   }

   async cacheUsers(forceUpdate) {
      if (!this.users || forceUpdate === true) {
         let users = await db.users.toArray();
         this.users = {};
         this.teachers = {};
         for (let user of users) {
            this.users[user.id] = user;
            if (user.type === 'teacher') this.teachers[user.id] = user;
         }
      }
   }

   async cacheUserGroups(forceUpdate) {
      if (!this.usersGroups || forceUpdate === true) {
         let userGroups = await db.userGroups.toArray();
         this.userGroups = {};
         for (let group of userGroups) {
            this.userGroups[group.id] = group;
         }
      }
   }

   async cacheAppointmentTypes(forceUpdate) {
      if (!this.appTypes || forceUpdate === true) {
         let appTypes = await db.appointmentTypes.toArray();
         this.appTypes = {};
         for (let appType of appTypes) {
            this.appTypes[appType.id] = appType;
         }
      }
   }

   async cachePupils(forceUpdate) {
      
      if (!this.pupils || forceUpdate === true) {
         let pupils = await db.users.where('type').equals('pupil').toArray();
         this.pupils = {};
         for (let pupil of pupils) {
            this.pupils[pupil.id] = pupil;
            const cls = this.getClass(pupil.classId);
            this.pupils[pupil.id].class = cls;
            cls.pupils.push(pupil.id);
            pupil.courses = [];
         }
      }

      const user = this.getUserSelf();

      if (user && user.type === 'teacher') {
         for (const i in this.courses) {
            const course = this.courses[i];

            for (const pupilId of course.pupils) {
               const pupil = this.getPupil(pupilId);
               if (pupil && pupil.id !== 0) {
                  pupil.courses.push(course);
               }
            }
         }
      }

   }

   async cacheClasses(forceUpdate) {
      if (!this.classes || forceUpdate === true) {
         let classes = await db.classes.toArray();
         this.classes = {};
         for (let cl of classes) {
            cl.pupils = [];
            cl.teachers = Object.values(cl.teachers);
            this.classes[cl.id] = cl;
         }
      }
   }

   async cacheRooms(forceUpdate) {
      if (!this.rooms || forceUpdate === true) {
         let rooms = await db.rooms.toArray();
         this.rooms = {};
         for (let room of rooms) this.rooms[room.id] = room;
      }
   }

   async cacheCourses(forceUpdate) {
      if (!this.courses || forceUpdate === true) {
         let courses = await db.courses.toArray();
         this.courses = {};
         for (let course of courses) this.courses[course.id] = course;
      }
   }

   async cacheCurrentYear(forceUpdate) {
      if (!this.currentYear || forceUpdate === true) {
         this.currentYear = await db.years.get({isCurrent: 1});
      }
   }

   async cacheTimetable(forceUpdate) {

      if (!this.timetable || forceUpdate === true) {
         this.timetable = await db.timetable.toArray();
         this.timetableTeachers = {};
         this.timetableClasses = {};
         this.timetablePupils = {};
         this.timetableRooms = {};
         this.timetableCourses = {};

         for (let ttItem of this.timetable) {
            const course = this.getCourse(ttItem.courseId);

            for (const teacherId of ttItem.teachers) {
               if (!this.timetableTeachers[teacherId]) this.timetableTeachers[teacherId] = [];
               this.timetableTeachers[teacherId].push(ttItem);
            }

            for (const roomId of ttItem.rooms) {
               if (!this.timetableRooms[roomId]) this.timetableRooms[roomId] = [];
               this.timetableRooms[roomId].push(ttItem);
            }

            if (course) {
               ttItem.course = course;
               if (!this.timetableCourses[course.id]) this.timetableCourses[course.id] = [];
               this.timetableCourses[course.id].push(ttItem);

               for (const classId of course.classes) {
                  if (!this.timetableClasses[classId]) this.timetableClasses[classId] = [];
                  this.timetableClasses[classId].push(ttItem);
               }
               if (course.pupils) {
                  for (const pupilId of course.pupils) {
                     if (!this.timetablePupils[pupilId]) this.timetablePupils[pupilId] = [];
                     this.timetablePupils[pupilId].push(ttItem);
                  }
               }
            }

         }
      }

   }


   getRoom(id) {
      if (!this.rooms) throw new Error("db-manager: no rooms cached");
      if (typeof id === 'string') id = parseInt(id);
      return this.rooms[id];
   }

   getRooms() {
      if (!this.rooms) throw new Error("db-manager: no rooms cached");
      return this.rooms;
   }

   getTimetableByTeacher(teacherId) {
      if (this.timetableTeachers.hasOwnProperty(teacherId)) {
         return this.timetableTeachers[teacherId];
      }
      else {
         return [];
      }
   }

   async getWatchesByTeacher(teacherId) {
      return db.watches.where('teacherId').equals(teacherId).toArray();
   }

   getTimetableByClass(classId) {
      return this.timetableClasses[classId];
   }

   getTimetableByRoom(roomId) {
      return this.timetableRooms[roomId];
   }

   getTimetableByCourse(courseId) {
      return this.timetableCourses[courseId];
   }

   getTimetableByPupil(pupilId) {
      return this.timetablePupils[pupilId];
   }

   getTeacherByShort(short) {
      if (!this.teachers) throw new Error("db-manager: no teachers cached");
      for (const teacherId in this.teachers) {
         if (this.teachers[teacherId].short === short) return this.teachers[teacherId];
      }
      return null;
   }

   getTeachers() {
      if (!this.teachers) throw new Error("db-manager: no teachers cached");
      return this.teachers;
   }

   getUser(id) {
      if (!this.users) throw new Error("db-manager: no users cached");
      if (typeof id === 'string') id = parseInt(id);
      if (this.users[id]) return this.users[id];
      else {
         return { id: 0, firstName: '?', lastName: '?', normedFirstName: '?', normedLastName: '?', short: '?', type: '', classId: 0, email: '' };
      }
   }

   getTutoredPupils(teacherId) {
      return Object.values(this.users).filter(user => user.type === 'pupil' && user.tutorId === teacherId);
   }

   getUsers() {
      if (!this.users) throw new Error("db-manager: no users cached");
      return this.users;
   }

   getUserGroups() {
      if (!this.userGroups) throw new Error("db-manager: no users groups cached");
      return this.userGroups;
   }

   getAppointmentTypes() {
      if (!this.appTypes) throw new Error("db-manager: no appointment types cached");
      return this.appTypes;
   }


   getPupil(id) {
      if (!this.pupils) throw new Error("db-manager: no pupils cached");
      if (typeof id === 'string') id = parseInt(id);
      if (this.pupils[id]) return this.pupils[id];
      else {
         return { id: 0, firstName: '?', lastName: '?', normedFirstName: '?', normedLastName: '?', short: '?', type: '', classId: 0, class: false, email: '' };
      }
   }

   getPupils() {
      if (!this.pupils) throw new Error("db-manager: no pupils cached");
      return this.pupils;
   }   

   getClass(id) {
      if (!this.classes) throw new Error("db-manager: no classes cached");
      if (typeof id === 'string') id = parseInt(id);
      return this.classes[id];
   }

   getClassName(id) {
      const cls = this.getClass(id);
      if (typeof cls !== 'object') return '?';
      else return cls.name;
   }

   getClassByName(name) {
      return Object.values(this.getClasses()).find(cls => cls.name === name);
   }

   getClasses() {
      if (!this.classes) throw new Error("db-manager: no classes cached");
      return this.classes;
   }

   getCourse(id) {
      if (!this.courses) throw new Error("db-manager: no courses cached");
      if (typeof id === 'string') id = parseInt(id);
      return this.courses[id];
   }

   getCourses() {
      if (!this.courses) throw new Error("db-manager: no courses cached");

      const user = this.getUserSelf();
      if (user.type === 'pupil') return this.getMyCourses();
      else if (user.type === 'teacher') return Object.values(this.courses);
   }

   getMyCourses() {
      if (!this.courses) throw new Error("db-manager: no courses cached");
      const user = this.getUserSelf();

      if (user.type === 'pupil') {
         return Object.values(this.courses).filter(course => { return course.pupils && course.pupils.includes(user.id); });
      }
      else if (user.type === 'teacher') {
         return Object.values(this.courses).filter(course => { return course.teachers && course.teachers.includes(user.id); });
      }
   }

   getFormTeacherClasses() {
      const user = this.getUserSelf();
      const classes = this.getClasses();
      return Object.values(classes).filter(cls => cls.formTeachers.includes(user.id));
   }

   getCalendarSettings() {
      return JSON.parse(localStorage.getItem('calSettings')) || {
         fontSize: 70,
         dayWidth: 100,
         dayHeight: 100,
         dayColumns: 0
      };
   }

   async getSubstData(filter, sortFunction) {

      let data = await db.substitutions.toArray();
      if (data == 'undefined' || data == null) return {};

      data = data.filter(item => item.subject !== 'Reserve' && !(item.absentClasses && item.absentClasses.length === 1 && ['P1', 'P2', 'P3'].includes(item.absentClasses[0])));
      try {
         data.sort(sortFunction);
      }
      catch(e) {
         logger.error(e);
         throw new AppDataError(e);
      }

      let days = {};
      for (let item of data) {
         if (filter(item)) {
            if (days[item.date] === undefined) days[item.date] = [];
            days[item.date].push(item);
         }
      } 

      return days;

   }

   getSubstAll(sortFunction) {
      return this.getSubstData(() => true, sortFunction);
   }


   getSubstByUser(userShort, sortFunction) {
      return this.getSubstData(
         item => item.affectedTeachers.map(x => x.toUpperCase()).includes(userShort.toUpperCase()),
         sortFunction
      );
   }

   getSubstByClass(className, sortFunction) {
      return this.getSubstData(
         item => item.affectedClasses.includes(className),
         sortFunction
      );
   }

   async getTagesplaene() {
      const list = await db.tagesplaene.toArray();

      let map = {};
      for (let plan of list) {
         if (map[plan.date] === undefined) map[plan.date] = [];
         map[plan.date].push(plan);
      }

      return map;

   }

   async getFiles() {
      return await this.sinRequest({ action: 'get-files' });
   }

   async changePassword(oldPwd, newPwd) {
      let user = this.getUserSelf();
      return await this.sinRequest({ action: 'usr-change-pwd', 'user-short': user.short, 'old-pwd': oldPwd, 'new-pwd': newPwd }, progress);
   }

   async courseAddPupils(courseId, pupils) {
      try {
         if (!this.online) throw new Error("Sie müssen online sein, um diese Aktion durchzuführen.");
         await this.sinRequest({ action: 'crs-pupil-add', 'courseId': courseId, 'pupilIds': pupils });
         const data = await this.getSyncData();
         if (data) await this.synchronize(data);
      }
      catch(e) {
         logger.error(e);
         toast.alert(e, { duration: 8000 });
      }
   }

   async courseRemovePupil(courseId, pupilId) {
      try {
         if (!this.online) throw new Error("Sie müssen online sein, um diese Aktion durchzuführen.");
         await this.sinRequest({ action: 'crs-pupil-remove', 'courseId': courseId, 'pupilIds': [pupilId] });
         const data = await this.getSyncData();
         await this.synchronize(data);
      }
      catch(e) {
         logger.error(e);
         toast.alert(e, { duration: 8000 });
      }

   }

   async deleteClassRegister(item) {

      let metaData = await this.getKeyValueData('classRegister') || { idMap: {}, yearId: null };      

      await this.deleteSyncItem(
         db.classRegister,
         'classreg-delete',
         metaData,
         item
      );

      await this.setKeyValueData('classRegister', metaData);

   }


   async saveClassRegister(item) {

      let metaData = await this.getKeyValueData('classRegister') || { idMap: {}, yearId: null };      

      await this.saveSyncItem(
         db.classRegister,
         'classreg-save',
         (item) => this.setupClassRegisterItem(item),
         metaData,
         item
      );

      await this.setKeyValueData('classRegister', metaData);

   }   

   async deleteClassNote(item) {

      let metaData = await this.getKeyValueData('classNotes') || { idMap: {}, yearId: null };      

      await this.deleteSyncItem(
         db.classNotes,
         'classnote-delete',
         metaData,
         item
      );

      await this.setKeyValueData('classNotes', metaData);

   }


   async saveClassNote(item) {

      let metaData = await this.getKeyValueData('classNotes') || { idMap: {}, yearId: null };      

      await this.saveSyncItem(
         db.classNotes,
         'classnote-save',
         false,
         metaData,
         item
      );

      await this.setKeyValueData('classNotes', metaData);

   }

   async deleteDayAbsence(item) {

      await this.sinRequest({
         action: 'day-absence-delete',
         ids: [item.id]
      });

      /*
      await this.deleteSyncItem(
         null,
         'day-absence-delete',
         null,
         item
      );
      */

   }


   async saveDayAbsence(item) {

      await this.sinRequest({
         action: 'day-absence-save',
         data: [item]
      });

      /*
      await this.saveSyncItem(
         null,
         'day-absence-save',
         (item) => this.setupDayAbsenceItem(item),
         null,
         item,
         false
      );
      */

   }

   async deletePupilNote(item) {

      let metaData = await this.getKeyValueData('pupilNotes') || { idMap: {}, yearId: null };      

      await this.deleteSyncItem(
         db.pupilNotes,
         'pupilnote-delete',
         metaData,
         item
      );

      await this.setKeyValueData('pupilNotes', metaData);

   }


   async savePupilNote(item) {

      let metaData = await this.getKeyValueData('pupilNotes') || { idMap: {}, yearId: null };      

      await this.saveSyncItem(
         db.pupilNotes,
         'pupilnote-save',
         (item) => this.setupPupilNoteItem(item),
         metaData,
         item,
         false
      );

      await this.setKeyValueData('pupilNotes', metaData);

   }

   async deleteSyncItem(dbTable, action, metaData, item) {

      // Wenn das Item lokal gespeichert werden soll
      if (!item.saveOnlineOnly) {
         item = await dbTable.get({localId: item.localId});

         // Wenn der Eintrag keine Externe ID hat: einfach löschen und abbrechen.
         if (!item.id) {
            dbTable.delete(item.localId);
            return;
         }

         // Daten zuerst lokal als zu löschen speichern
         await dbTable.update(item.localId, {syncStatus: 'delete'});

         // Wenn eine Internetverbindung besteht: Daten auf dem Server löschen und bei Erfolg auch lokal löschen
         if (item.id && this.online) {
            try {         
               let response = await this.sinRequest({
                  action: action,
                  ids: [item.id]
               });
               if (response.status === 0 && response.data && response.data.length === 1) {
                  let serverItem = response.data[0];
                  if (serverItem.syncStatus === 'error') {
                     logger.error(serverItem.errorMessage);
                     toast.alert(serverItem.errorMessage, { duration: 8000 });
                     await dbTable.update(item.localId, {syncStatus: 'error', errorMessage: serverItem.errorMessage });
                  }
                  else {
                     await dbTable.delete(item.localId);
                     delete metaData.idMap[item.localId];
                  }
               }
               else {
                  toast.alert("Der Eintrag konnte nicht vom Server gelöscht werden.", { duration: 8000 });
               }

            }
            catch(e) {
               logger.error(e);
               toast.alert(e, { duration: 8000 });
            }
         }

      }

      // Wenn das item nur online gespeichert/gelöscht werden soll
      else {
         if (this.online) {
            try {         
               let response = await this.sinRequest({
                  action: action,
                  ids: [item.id]
               });
               if (response.status === 0 && response.data && response.data.length === 1) {
                  let serverItem = response.data[0];
                  if (serverItem.syncStatus === 'error') throw serverItem.errorMessage;
               }
               else {
                  toast.alert("Der Eintrag konnte nicht vom Server gelöscht werden.", { duration: 8000 });
               }

            }
            catch(e) {
               logger.error(e);
               toast.alert(e, { duration: 8000 });
            }
         }
         else {
            const msg = "Dieser Eintrag kann nur bei bestehender Internetverbindung gelöscht werden.";
            logger.error(msg);
            toast.alert(msg, { duration: 8000 });
         }
      }

   }

   async saveSyncItem(dbTable, action, setupFunc, metaData, item, saveOnServerError=true) {

      // Daten zuerst lokal speichern bzw. aktualisieren
      item.syncStatus = "update";

      // Wenn das Item bereits vorhanden ist: zwischenspeichern
      let oldItem = false;
      if (item.localId) oldItem = await dbTable.where({ localId: item.localId }).first();

      // Item direkt speichern (notwendig, damit es eine localId erhält)
      if (!item.saveOnlineOnly) await dbTable.put(item);

      // Wenn eine Internetverbindung besteht: Daten auf den Server hochladen und die zurückgegebenen Daten wieder lokal speichern
      if (this.online) {

         document.querySelector('.sync-all').classList.toggle('rotating', true);
         
         try {
            let response = await this.sinRequest({
               action: action,
               data: [item]
            });
            if (response.status === 0 && response.data && response.data.length === 1) {
               let serverItem = response.data[0];
               if (serverItem.syncStatus === 'error') {
                  toast.alert(serverItem.errorMessage, { duration: 8000 });
               }

               if (!item.saveOnlineOnly) {
                  if (setupFunc) setupFunc(serverItem);
                  await dbTable.put(serverItem);
                  metaData.idMap[serverItem.id] = item.localId;
               }
            }
            else {
               toast.alert("Der Eintrag konnte nicht auf den Server übertragen werden.", { duration: 8000 });
            }

            document.querySelector('.sync-all').classList.toggle('rotating', false);
            return true;
   
         }
         catch(e) {
            document.querySelector('.sync-all').classList.toggle('rotating', false);
            logger.error(e);
            toast.alert(e, { duration: 8000 });
            // wenn bei einem ServerError das Item nicht gespeichert werden soll: alte Version wiederherstellen, falls vorhanden
            if (!saveOnServerError && oldItem) await dbTable.put(oldItem);

            return false;
         }
   
      }
      else {
         // nur speichern, wenn offline-Speichern gewünscht ist.
         if (!item.saveOnlineOnly) {
            await dbTable.put(item);
         }
         // andernfalls: item Löschen, wenn es nicht auf dem Server vorhanden ist bzw. alte Version wieder herstellen
         else {
            const msg = "Das Speichern dieses Eintrags ist nur möglich, wenn eine Internetverbindung besteht.";
            logger.error(msg);
            toast.alert(msg, { duration: 8000 });
            if (!item.id) dbTable.delete(item.localId);
            else if (oldItem) await dbTable.put(oldItem);
         }
      }

   }

   async getClassRegisterItems(selector) {

      return await db.classRegister.where(selector).toArray();

   }

   async getClassRegisterItem(selector) {

      return await db.classRegister.where(selector).first();

   }

   /*
   async getClassNoteItem(id) {
      const item = await db.classNotes.where({ id: id }).toArray();
      if (item.length === 0) return false;
      return item[0];
   }
   */

   async getClassNotes(classId, isoDate) {

      if (Array.isArray(isoDate)) {
         let from = isoDate[0], to = isoDate[1];
         return (await db.classNotes
            .filter(item => item.classes.includes(classId) &&
               (item.dateFrom >= from && item.dateFrom <= to) || (item.dateTo >= from && item.dateTo <= to)
            ).toArray());
      }
      else {
         return (await db.classNotes
            .filter(item => item.classes.includes(classId) && (isoDate >= item.dateFrom && isoDate <= item.dateTo)).toArray());
      }

   }

   async cacheClassRegisterDayData(data) {

      data.time = Date.now();
      data.dateClassKey = data.date + '#' + data.classId;

      await db.classRegisterDayCache.put(data);

      if (await db.classRegisterDayCache.count() > this.dayCacheMaxSize) {
         let itemToDelete = false;
         let oldestTime = false;
         await db.classRegisterDayCache.each(item => {
            if (oldestTime === false || item.time < oldestTime) {
               itemToDelete = item.dateClassKey;
               oldestTime = item.time;
            }
         });
         await db.classRegisterDayCache.where('date').equals(dateToDelete).delete();
      }

      return data;

   }

   async getClassRegisterDayDataFromCache(date, classId) {
      let data = await db.classRegisterDayCache.where({ dateClassKey: date + '#' + classId }).toArray();
      if (data.length === 0) {
         data = { classRegister: [], classNotes: [], dayAbsences: [] };
      }
      else {
         data = data[0];
      }
      data.fromCache = true;
      data.classRegister = data.classRegister.filter(item => item.creatorId !== this.user.id)
      .concat(await this.getClassRegisterItems({ creatorId: this.user.id, date: date, classes: classId }));

      data.classNotes = data.classNotes.filter(item => item.creatorId !== this.user.id)
      .concat(await this.getClassNotes(classId, date));
      return data;
   }

   /**
    *  Ermittelt die Klassenbuchdaten (ClassRegisterItems, Notes, DayAbsences) eines Tages einer Klasse.
    *  Sofern keine Internetverbindung möglich ist, werden gecachte Daten aus der Datenbank zurückgegeben.
    *  Beim Übertragen der Daten werden Einträge des Benutzers entfernt und aus der Datenbank geladen.
    * @param {*} date    das Datum
    * @param {*} classId die ID der Klasse
    * @returns 
    */

   async getClassRegisterDayData(date, classId) {

      if (!this.online) return this.getClassRegisterDayDataFromCache(date, classId);
      try {
         let request = await this.sinRequest({ 'action': 'classreg-get-day-data', 'class-id': classId, 'date': date });
         if (request.data && request.data.date && request.data.classId) {
            request.data.classRegister = request.data.classRegister.map(item => this.setupClassRegisterItem(item))
               .filter(item => item.creatorId !== this.user.id)
               .concat(await this.getClassRegisterItems({ creatorId: this.user.id, date: date, classes: classId }));

            request.data.dayAbsences.map(item => this.setupDayAbsenceItem(item));

            request.data.classNotes = request.data.classNotes.filter(item => item.creatorId !== this.user.id)
               .concat(await this.getClassNotes(classId, date));

            this.cacheClassRegisterDayData(request.data);
         }
         return request.data;
      }
      catch (e) {
         logger.error(e);
         toast.alert(e, { duration: 8000 });
         return this.getClassRegisterDayDataFromCache(date, classId);
      }
   }

   async getDayAbsences(selector) {

      let request = await this.sinRequest({ 'action': 'day-absence-get-items', 'class-id': selector.classId, 'date': selector.date });
      let items = request.data.map(item => {
         item.saveOnlineOnly = true;
         return item;
      });
      return items;

   }

   async getDayAbsence(id) {
      let request = await this.sinRequest({ 'action': 'day-absence-get', 'id': id });
      let abs = request.data;
      this.setupDayAbsenceItem(abs);
      abs.saveOnlineOnly = true;
      return abs;
   }

   async getTicker() {
      return await this.getKeyValueData('ticker') || '';
   }

   setupClassRegisterItem(item) {

      if (item.studyGroupType === 'course') {
         item.courses = item.studyGroups;
         item.classes = [];
         item.courses.map(id => {
            if (this.getCourse(id)) {
               item.classes = item.classes.concat(this.getCourse(id).classes).unique();
            }
         });
      }
      else {
         item.classes = item.studyGroups;
      }

      return item;

   }

   setupDayAbsenceItem(item) {
      item.classId = this.getUser(item.pupilId).classId;
   }

   setupPupilNoteItem(item) {
      item.classId = this.getUser(item.pupilId).classId;
   }

   saveMyClasses(classes) {
      localStorage.setItem('myClasses', classes);
   }

   saveCalFilters(filters) {
      localStorage.setItem('calFilters', JSON.stringify(filters));
   }

   saveCalendarSettings(settings) {
      localStorage.setItem('calSettings', JSON.stringify(settings));
   }

   async getPupilNoteTemplates() {
      let data = await this.getKeyValueData('pupilNoteTemplates');
      return data ? JSON.parse(data) : [];
   }

   getLocalStorage() {
      let data = {};
      for (var i = 0; i < localStorage.length; i++){
         const key = localStorage.key(i);
         data[key] = key === 'user' ? JSON.parse(localStorage.getItem(key)) : localStorage.getItem(key);
      }
      return data;
   }

   async getLocalDatabase() {
      let data = {};
      let tableNames = [];
      db.tables.forEach((table) => tableNames.push(table.name));

      for (let tableName of tableNames) {
         data[tableName] = await db[tableName].toArray();
      };      

      return data;
   }

   validateSinResponse(response) {

      if ('status' in response == false) return false;
      if (response.status != 0) return false;
      if ('data' in response == false) return false;

      return true;

   }

   async sinFileUpload(formData, progress, success) {

      return new Promise((resolve, reject) => {

         if (localStorage.getItem('authToken')) formData.append('auth-token', localStorage.getItem('authToken'));
         formData.append('client-id', localStorage.getItem('clientId'));
         formData.append('app-version', localStorage.getItem('appVersion'));
         formData.append('action', 'file-upload');


         let request = new XMLHttpRequest();
         request.open('POST', this.sinServer); 

         // upload progress event
         request.upload.addEventListener('progress', (e) => {
            // upload progress as percentage
            let completed = (e.loaded / e.total);
            progress(completed);
         });

         // request finished event
         request.addEventListener('load', () => {
            if (request.status >= 200 && request.status < 300) {

               try {
                  let response = JSON.parse(request.response);
                  if (!this.validateSinResponse(response)) reject(response.errors.join("\n"));
                  else {
                     success();
                     resolve(response);                  
                  }
               }
               catch(e) {
                  reject(e);
               }

            }
            else {
               reject(request.statusText);
            }
         });

         request.addEventListener('error', () => {
            reject(request.statusText);
         });

         // send POST request to server
         request.send(formData);

      });

   }

   async sinRequest(params, progress, allowDebug=true) {

      const user = this.getUserSelf();
      params['user-hash'] =  user && user.hash ? user.hash : 0;
      
      if (localStorage.getItem('authToken')) params['auth-token'] = localStorage.getItem('authToken');
      params['client-id']   = localStorage.getItem('clientId');
      params['client']      = 'thg-app';
      params['app-version'] = localStorage.getItem('appVersion') || '';

      if (progress) progress.setValue(0, 0, 'Lade Daten herunter...');
      
      const controller = new AbortController();
      let timeoutId = setTimeout(() => {
         controller.abort();
         timeoutId = false;
      }, 10000);

      const response = await fetch(this.sinServer, {
         signal: controller.signal,
         method: 'POST',
         mode: 'cors',
         cache: 'no-cache',
         credentials: 'same-origin',
         headers: {
           'Content-Type': 'sin-transfer-protocol'
         },
         redirect: 'follow',
         referrerPolicy: 'no-referrer',
         body: JSON.stringify(params)
      });

      if (timeoutId) clearTimeout(timeoutId);

      if (!response.ok) throw new Error("Verbindungsfehler: " + await response.text());

      const reader = response.body.getReader();
      const contentLength = +response.headers.get('Content-Length');

      let receivedLength = 0;
      let chunks = [];

      while(true) {
         const {done, value} = await reader.read();
         if (done) break;

         chunks.push(value);
         receivedLength += value.length;
         if (progress) {
            progress.setValue((receivedLength / contentLength) * 100, Math.floor(receivedLength / 1024) + ' kB');
            if (receivedLength > 1000) progress.show(true);
         }
      }

      let chunksAll = new Uint8Array(receivedLength);
      let position = 0;
      for(let chunk of chunks) {
         chunksAll.set(chunk, position);
         position += chunk.length;
      }

      let result = new TextDecoder("utf-8").decode(chunksAll);

      /*
      if (this.getDebugMode() && allowDebug) {
         this.sinRequest({ action: 'debug-log-save', type: 'request', request: params, response: result, localStorage: this.getLocalStorage() }, false, false);
      }
      */

      let commits = null;
      try {
         commits = JSON.parse(result);
      }
      catch(e) {
         throw new Error(e);
      }


      if (commits.notes && commits.notes.length > 0) {
         toast.message(commits.notes.join("\n"));         
      }
      if (commits.warnings && commits.warnings.length > 0) {
         toast.message(commits.warnings.join("\n"));         
      }


      console.log(commits);

      if (commits.status && commits.status === 3) {
         new ModalConfirm(
            "Es liegt eine neue Version vor. Vor dem Aktualisieren der Daten muss diese installiert werden. Soll die neue Version jetzt installiert werden?",
            async (confirmed) => {
               if (confirmed) window.location.reload(true);
            }
         );
      }

      else if (!this.validateSinResponse(commits)) {

         //logger.error(commits.errors.join("\n"));
         //toast.alert(commits.errors.join("\n"));
         throw new Error(commits.errors.join("\n"));
         return false;
      }
      else return commits;

   }

}
